import supabase from '@/utils/supabase';
import type {
  OnboardingAwareCenter,
  OnboardingBusinessProfile,
  OnboardingState,
} from '../model/types';
import {
  buildBusinessProfileSummary,
  normalizeBusinessProfile,
  normalizeOnboardingState,
} from '../model/helpers';

const ONBOARDING_SELECT = 'id, name, description, type, onboarding_state, onboarding_business_profile';
const BASE_CENTER_SELECT = 'id, name, description, type';
const FALLBACK_STORAGE_PREFIX = 'center-onboarding-fallback';
let onboardingColumnsAvailable: boolean | null = null;

const normalizeCenterPayload = (center: OnboardingAwareCenter): OnboardingAwareCenter => ({
  ...center,
  onboarding_state: normalizeOnboardingState(center.onboarding_state),
  onboarding_business_profile: normalizeBusinessProfile(center.onboarding_business_profile),
});

const isMissingOnboardingColumnError = (error: { message?: string } | null | undefined): boolean => {
  const message = error?.message?.toLowerCase() ?? '';

  return (
    message.includes('onboarding_state') ||
    message.includes('onboarding_business_profile')
  ) && (
    message.includes('does not exist') ||
    message.includes('could not find')
  );
};

const getFallbackStorageKey = (centerId: string) => `${FALLBACK_STORAGE_PREFIX}:${centerId}`;

const readFallbackState = (centerId: string): Pick<
  OnboardingAwareCenter,
  'onboarding_state' | 'onboarding_business_profile'
> => {
  if (typeof window === 'undefined') {
    return {
      onboarding_state: normalizeOnboardingState(null),
      onboarding_business_profile: normalizeBusinessProfile(null),
    };
  }

  try {
    const rawValue = window.localStorage.getItem(getFallbackStorageKey(centerId));
    if (!rawValue) {
      return {
        onboarding_state: normalizeOnboardingState(null),
        onboarding_business_profile: normalizeBusinessProfile(null),
      };
    }

    const parsed = JSON.parse(rawValue);

    return {
      onboarding_state: normalizeOnboardingState(parsed?.onboarding_state),
      onboarding_business_profile: normalizeBusinessProfile(parsed?.onboarding_business_profile),
    };
  } catch {
    return {
      onboarding_state: normalizeOnboardingState(null),
      onboarding_business_profile: normalizeBusinessProfile(null),
    };
  }
};

const writeFallbackState = (
  centerId: string,
  payload: Pick<OnboardingAwareCenter, 'onboarding_state' | 'onboarding_business_profile'>
) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    getFallbackStorageKey(centerId),
    JSON.stringify({
      onboarding_state: normalizeOnboardingState(payload.onboarding_state),
      onboarding_business_profile: normalizeBusinessProfile(payload.onboarding_business_profile),
    })
  );
};

export class CenterOnboardingService {
  private async getBaseCenter(centerId: string): Promise<OnboardingAwareCenter> {
    const { data, error } = await supabase
      .from('Center')
      .select(BASE_CENTER_SELECT)
      .eq('id', centerId)
      .single();

    if (error || !data) {
      throw new Error(error?.message || 'Failed to load center data');
    }

    return data as OnboardingAwareCenter;
  }

  private async getFallbackCenter(centerId: string): Promise<OnboardingAwareCenter> {
    const baseCenter = await this.getBaseCenter(centerId);
    const fallbackState = readFallbackState(centerId);

    return normalizeCenterPayload({
      ...baseCenter,
      ...fallbackState,
    });
  }

  async getOnboarding(centerId: string): Promise<OnboardingAwareCenter> {
    if (onboardingColumnsAvailable === false) {
      return this.getFallbackCenter(centerId);
    }

    const { data, error } = await supabase
      .from('Center')
      .select(ONBOARDING_SELECT)
      .eq('id', centerId)
      .single();

    if (isMissingOnboardingColumnError(error)) {
      onboardingColumnsAvailable = false;
      return this.getFallbackCenter(centerId);
    }

    if (error || !data) {
      throw new Error(error?.message || 'Failed to load onboarding data');
    }

    onboardingColumnsAvailable = true;
    return normalizeCenterPayload(data as OnboardingAwareCenter);
  }

  async updateBusinessProfile(
    centerId: string,
    businessProfile: OnboardingBusinessProfile,
    onboardingState: OnboardingState
  ): Promise<OnboardingAwareCenter> {
    if (onboardingColumnsAvailable === false) {
      const { error: baseUpdateError } = await supabase
        .from('Center')
        .update({
          description: buildBusinessProfileSummary(businessProfile),
        })
        .eq('id', centerId);

      if (baseUpdateError) {
        throw new Error(baseUpdateError.message || 'Failed to save business profile');
      }

      writeFallbackState(centerId, {
        onboarding_state: onboardingState,
        onboarding_business_profile: businessProfile,
      });

      return this.getFallbackCenter(centerId);
    }

    const description = buildBusinessProfileSummary(businessProfile);
    const { data, error } = await supabase
      .from('Center')
      .update({
        onboarding_business_profile: businessProfile,
        onboarding_state: onboardingState,
        description,
      })
      .eq('id', centerId)
      .select(ONBOARDING_SELECT)
      .single();

    if (isMissingOnboardingColumnError(error)) {
      onboardingColumnsAvailable = false;
      const { error: baseUpdateError } = await supabase
        .from('Center')
        .update({ description })
        .eq('id', centerId);

      if (baseUpdateError) {
        throw new Error(baseUpdateError.message || 'Failed to save business profile');
      }

      writeFallbackState(centerId, {
        onboarding_state: onboardingState,
        onboarding_business_profile: businessProfile,
      });

      return this.getFallbackCenter(centerId);
    }

    if (error || !data) {
      throw new Error(error?.message || 'Failed to save business profile');
    }

    onboardingColumnsAvailable = true;
    return normalizeCenterPayload(data as OnboardingAwareCenter);
  }

  async updateProgress(centerId: string, onboardingState: OnboardingState): Promise<OnboardingAwareCenter> {
    if (onboardingColumnsAvailable === false) {
      const fallbackState = readFallbackState(centerId);
      writeFallbackState(centerId, {
        onboarding_state: onboardingState,
        onboarding_business_profile: normalizeBusinessProfile(fallbackState.onboarding_business_profile),
      });

      return this.getFallbackCenter(centerId);
    }

    const { data, error } = await supabase
      .from('Center')
      .update({ onboarding_state: onboardingState })
      .eq('id', centerId)
      .select(ONBOARDING_SELECT)
      .single();

    if (isMissingOnboardingColumnError(error)) {
      onboardingColumnsAvailable = false;
      const fallbackState = readFallbackState(centerId);
      writeFallbackState(centerId, {
        onboarding_state: onboardingState,
        onboarding_business_profile: normalizeBusinessProfile(fallbackState.onboarding_business_profile),
      });

      return this.getFallbackCenter(centerId);
    }

    if (error || !data) {
      throw new Error(error?.message || 'Failed to save onboarding progress');
    }

    onboardingColumnsAvailable = true;
    return normalizeCenterPayload(data as OnboardingAwareCenter);
  }

  async complete(centerId: string, onboardingState: OnboardingState): Promise<OnboardingAwareCenter> {
    if (onboardingColumnsAvailable === false) {
      const fallbackState = readFallbackState(centerId);
      writeFallbackState(centerId, {
        onboarding_state: onboardingState,
        onboarding_business_profile: normalizeBusinessProfile(fallbackState.onboarding_business_profile),
      });

      return this.getFallbackCenter(centerId);
    }

    const { data, error } = await supabase
      .from('Center')
      .update({ onboarding_state: onboardingState })
      .eq('id', centerId)
      .select(ONBOARDING_SELECT)
      .single();

    if (isMissingOnboardingColumnError(error)) {
      onboardingColumnsAvailable = false;
      const fallbackState = readFallbackState(centerId);
      writeFallbackState(centerId, {
        onboarding_state: onboardingState,
        onboarding_business_profile: normalizeBusinessProfile(fallbackState.onboarding_business_profile),
      });

      return this.getFallbackCenter(centerId);
    }

    if (error || !data) {
      throw new Error(error?.message || 'Failed to complete onboarding');
    }

    onboardingColumnsAvailable = true;
    return normalizeCenterPayload(data as OnboardingAwareCenter);
  }
}
